import { nextRuleBoxRefreshAt } from "@loot-os/api-contract";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** How long ago `at` was, in words: "just now", "5 minutes ago", "3 hours ago", "2 days ago". */
export function formatAgo(at: Date, now: Date): string {
  const ms = Math.max(0, now.getTime() - at.getTime());
  if (ms < MINUTE) return "just now";
  const [n, unit] =
    ms < HOUR
      ? [Math.floor(ms / MINUTE), "minute"]
      : ms < DAY
        ? [Math.floor(ms / HOUR), "hour"]
        : [Math.floor(ms / DAY), "day"];
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

/** A moment as the merchant's own date and time, for a hover title. */
export function formatExact(at: Date): string {
  return at.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** A wait as "h:mm", minutes rounded up, so it reads 0:01 until the moment it is over. */
export function formatCountdown(ms: number): string {
  const minutes = Math.max(0, Math.ceil(ms / MINUTE));
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
}

/**
 * When the next daily refresh is due: the server's answer while it is ahead of `now`, else the
 * contract's next refresh hour, so a list loaded before the refresh ran never counts below zero.
 */
export function nextRefreshAfter(serverNext: string, now: Date): Date {
  const next = new Date(serverNext);
  return Number.isFinite(next.getTime()) && next.getTime() > now.getTime()
    ? next
    : nextRuleBoxRefreshAt(now);
}

/** A box row's refresh lines: "Refreshed 3 hours ago" over "Next in 4:12". */
export function describeRefresh(
  refresh: { refreshedAt: string | null; nextRefreshAt: string },
  now: Date,
): { last: string; next: string } {
  const next = nextRefreshAfter(refresh.nextRefreshAt, now);
  return {
    last: refresh.refreshedAt
      ? `Refreshed ${formatAgo(new Date(refresh.refreshedAt), now)}`
      : "Not refreshed yet",
    next: `Next in ${formatCountdown(next.getTime() - now.getTime())}`,
  };
}
